import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';

const EVENT_TYPE_COLORS = {
	competition: '#8B4513',
	clinic: '#1A3C34',
	show: '#B8860B',
	auction: '#7B1F1F',
	meetup: '#4A6D5A',
	race: '#9B2C2C',
	course: '#5B7C99',
	other: '#666055',
};

function startOfMonth(y, m) { return new Date(y, m, 1); }
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function isoDate(y, m, d) { return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }

/**
 * Monthly calendar view for events.
 * `events` should have {id, slug, title, start_date, end_date, event_type, is_featured, price, is_free, location_name}
 * `onDayClick(iso)` optional — called when a day cell is clicked.
 * `selectedDate` optional ISO date to highlight.
 */
export default function EventCalendar({ events = [], onDayClick, selectedDate = null }) {
	const { t, i18n } = useTranslation();
	const localeTag = (i18n.language || 'en').split('@')[0];
	const today = new Date();
	const [year, setYear] = useState(today.getFullYear());
	const [month, setMonth] = useState(today.getMonth()); // 0-indexed

	const monthLabel = useMemo(() => {
		return new Date(year, month, 1).toLocaleDateString(localeTag, { month: 'long', year: 'numeric' });
	}, [year, month, localeTag]);

	// Group events by date — an event that spans multi-day appears on every day in [start_date, end_date]
	const eventsByDate = useMemo(() => {
		const map = {};
		for (const e of events) {
			if (!e.start_date) continue;
			const start = e.start_date;
			const end = e.end_date || e.start_date;
			let cur = new Date(start + 'T00:00:00');
			const stopAt = new Date(end + 'T00:00:00');
			let guard = 0;
			while (cur <= stopAt && guard < 400) {
				const key = isoDate(cur.getFullYear(), cur.getMonth(), cur.getDate());
				(map[key] = map[key] || []).push(e);
				cur.setDate(cur.getDate() + 1);
				guard += 1;
			}
		}
		return map;
	}, [events]);

	const days = useMemo(() => {
		const total = daysInMonth(year, month);
		const first = startOfMonth(year, month);
		// Sunday=0 ... treat Monday as first day (Europe)
		const shift = (first.getDay() + 6) % 7;
		const cells = [];
		for (let i = 0; i < shift; i++) cells.push(null);
		for (let d = 1; d <= total; d++) cells.push(d);
		while (cells.length % 7 !== 0) cells.push(null);
		return cells;
	}, [year, month]);

	const prevMonth = () => {
		if (month === 0) {
			setYear((y) => y - 1);
			setMonth(11);
		} else setMonth((m) => m - 1);
	};
	const nextMonth = () => {
		if (month === 11) {
			setYear((y) => y + 1);
			setMonth(0);
		} else setMonth((m) => m + 1);
	};
	const goToday = () => {
		setYear(today.getFullYear());
		setMonth(today.getMonth());
		onDayClick?.(isoDate(today.getFullYear(), today.getMonth(), today.getDate()));
	};

	const todayISO = isoDate(today.getFullYear(), today.getMonth(), today.getDate());
	const weekLabels = useMemo(() => {
		// Locale-aware, Monday first
		const ref = new Date(2024, 0, 1); // Monday
		return Array.from({ length: 7 }, (_, i) => {
			const d = new Date(ref);
			d.setDate(ref.getDate() + i);
			return d.toLocaleDateString(localeTag, { weekday: 'short' });
		});
	}, [localeTag]);

	return (
		<div className="bg-white border border-[#E6E2D6] rounded-sm" data-testid="event-calendar">
			{/* Header */ }
			<div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E2D6] flex-wrap gap-2">
				<div className="flex items-center gap-2">
					<button onClick={ prevMonth } className="p-2 hover:bg-[#F2F0E9] rounded-sm" data-testid="cal-prev-month"
									aria-label="Previous month">
						<ChevronLeft className="w-4 h-4 text-[#1A3C34]"/>
					</button>
					<h3
						className="text-lg font-medium text-[#1A3C34] font-['Playfair_Display'] min-w-[9rem] text-center capitalize"
						data-testid="cal-month-label">{ monthLabel }</h3>
					<button onClick={ nextMonth } className="p-2 hover:bg-[#F2F0E9] rounded-sm" data-testid="cal-next-month"
									aria-label="Next month">
						<ChevronRight className="w-4 h-4 text-[#1A3C34]"/>
					</button>
				</div>
				<Button variant="outline" size="sm" onClick={ goToday } className="border-[#E6E2D6] rounded-sm h-8 text-xs"
								data-testid="cal-today-btn">
					<CalendarDays className="w-3 h-3 mr-1"/> { t('event.today', 'Today') }
				</Button>
			</div>

			{/* Weekday labels */ }
			<div className="grid grid-cols-7 border-b border-[#E6E2D6] bg-[#F9F7F2]">
				{ weekLabels.map((w) => (
					<div key={ w }
							 className="text-[10px] uppercase tracking-wider text-[#666055] font-semibold text-center py-2">{ w }</div>
				)) }
			</div>

			{/* Day cells */ }
			<div className="grid grid-cols-7">
				{ days.map((d, idx) => {
					if (d === null) {
						return <div key={ idx } className="min-h-[90px] border-r border-b border-[#F2F0E9] bg-[#FAF9F5]/60"/>;
					}
					const iso = isoDate(year, month, d);
					const isToday = iso === todayISO;
					const isSelected = iso === selectedDate;
					const list = eventsByDate[iso] || [];
					return (
						<button
							key={ idx }
							onClick={ () => onDayClick?.(iso) }
							className={ `min-h-[90px] text-left border-r border-b border-[#F2F0E9] p-1.5 transition-colors relative ${ isSelected ? 'bg-[#F2F0E9] ring-1 ring-inset ring-[#8B4513]' : 'hover:bg-[#F9F7F2]' }` }
							data-testid={ `cal-day-${ iso }` }
						>
							<div
								className={ `flex items-center justify-between text-xs font-semibold ${ isToday ? 'text-[#8B4513]' : 'text-[#1A3C34]' }` }>
								<span
									className={ isToday ? 'bg-[#8B4513] text-white w-6 h-6 rounded-full flex items-center justify-center text-[11px]' : '' }>{ d }</span>
								{ list.length > 3 && (
									<span className="text-[10px] text-[#666055]">+{ list.length - 3 }</span>
								) }
							</div>
							<div className="mt-1 space-y-0.5">
								{ list.slice(0, 3).map((e) => {
									const color = EVENT_TYPE_COLORS[e.event_type] || EVENT_TYPE_COLORS.other;
									return (
										<Link
											key={ e.id }
											to={ `/events/${ e.slug || e.id }` }
											state={{ from: { path: '/events?view=calendar' }, label: t('nav.events') } }
											onClick={ (evt) => evt.stopPropagation() }
											className="block text-[10px] leading-tight px-1.5 py-0.5 rounded-sm text-white truncate hover:opacity-90"
											style={ { backgroundColor: color } }
											title={ e.title }
											data-testid={ `cal-event-${ e.id }` }
										>
											{ e.title }
										</Link>
									);
								}) }
							</div>
						</button>
					);
				}) }
			</div>

			{/* Legend */ }
			<div
				className="flex items-center justify-start gap-3 flex-wrap px-4 py-3 border-t border-[#E6E2D6] bg-[#FAF9F5]/60">
				<span
					className="text-[10px] uppercase tracking-wider text-[#666055] font-semibold">{ t('event.legend', 'Legend') }:</span>
				{ Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
					<span key={ type } className="inline-flex items-center gap-1.5 text-[11px] text-[#3a3a3a]">
            <span className="w-2.5 h-2.5 rounded-sm" style={ { backgroundColor: color } }/>
						{ t(`event.type.${ type }`) }
          </span>
				)) }
			</div>
		</div>
	);
}